import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/client';
import { toast } from 'react-toastify';
import CompanyLogo from '../components/CompanyLogo';

const Login = () => {
  // 'login' | 'forgot' | 'forgot-sent'
  const [mode, setMode] = useState('login');

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Forgot password form state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('Login successful!');
      setTimeout(() => {
        if (result.mustChangePassword) {
          navigate('/change-password');
        } else if (result.role === 'admin') {
          navigate('/admin');
        } else if (result.role === 'tenant') {
          navigate('/tenant');
        } else {
          // Any custom staff-type role (accountant, collections officer, etc.)
          // lands in the Staff Portal, same as plain 'staff' — its sidebar is
          // gated per-module by that role's configured permissions.
          navigate('/staff');
        }
      }, 300);
    } else {
      setLoginError(result.message);
    }

    setLoginLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post('/auth/forgot-password', { email: forgotEmail });
    } catch {
      // Intentionally swallowed — API always returns generic success to prevent enumeration
    } finally {
      setForgotLoading(false);
      setMode('forgot-sent');
    }
  };

  const switchToForgot = () => {
    setLoginError('');
    // Pre-fill forgot email with whatever the user typed in the login form
    if (email) setForgotEmail(email);
    setMode('forgot');
  };

  const backToLogin = () => {
    setMode('login');
    setForgotEmail('');
  };

  const logoBox = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3,
        bgcolor: '#0b1220',
        borderRadius: 2,
        py: 3,
        px: 3,
      }}
    >
      <CompanyLogo height={84} />
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(160deg, #eef2f9 0%, #f4f6f9 45%, #e8edf5 100%)',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper elevation={0} sx={{ p: 4, width: '100%', border: '1px solid', borderColor: 'divider', boxShadow: '0 8px 28px rgba(15,23,42,0.10)' }}>

          {/* ── LOGIN VIEW ─────────────────────────────────────── */}
          {mode === 'login' && (
            <>
              {logoBox}
              <Typography component="h1" variant="h6" align="center" sx={{ fontWeight: 700, mb: 0.5 }}>
                Sign in to your account
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
                Property Master
              </Typography>

              {loginError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {loginError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleLoginSubmit} sx={{ mt: 2 }}>
                <TextField
                  margin="normal" required fullWidth
                  id="email" label="Email Address" name="email" type="email"
                  autoComplete="email" autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  margin="normal" required fullWidth
                  name="password" label="Password" type="password"
                  id="password" autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="submit" fullWidth variant="contained"
                  size="large" sx={{ mt: 3, mb: 1 }}
                  disabled={loginLoading}
                >
                  {loginLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box textAlign="center">
                <Link
                  component="button"
                  variant="body2"
                  onClick={switchToForgot}
                  sx={{ cursor: 'pointer' }}
                >
                  Forgot your password?
                </Link>
              </Box>
            </>
          )}

          {/* ── FORGOT PASSWORD VIEW ────────────────────────────── */}
          {mode === 'forgot' && (
            <>
              {logoBox}
              <Typography component="h2" variant="h6" align="center" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a temporary password.
              </Typography>

              <Box component="form" onSubmit={handleForgotSubmit} sx={{ mt: 1 }}>
                <TextField
                  margin="normal" required fullWidth autoFocus
                  type="email" label="Email Address"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
                <Button
                  type="submit" fullWidth variant="contained"
                  size="large" sx={{ mt: 2, mb: 1 }}
                  disabled={forgotLoading}
                >
                  {forgotLoading ? 'Sending...' : 'Send Temporary Password'}
                </Button>
              </Box>

              <Box textAlign="center" sx={{ mt: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={backToLogin}
                  sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to Sign In
                </Link>
              </Box>
            </>
          )}

          {/* ── FORGOT PASSWORD SENT VIEW ───────────────────────── */}
          {mode === 'forgot-sent' && (
            <>
              {logoBox}
              <Box textAlign="center" sx={{ mb: 2 }}>
                <MarkEmailReadIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography component="h2" variant="h6" gutterBottom>
                  Check Your Email
                </Typography>
              </Box>

              <Alert severity="success" sx={{ mb: 2 }}>
                If an account with that email exists, a temporary password has been sent.
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Check your inbox for an email containing your temporary password and login link.
                You will be prompted to set a new password after signing in.
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                If you don't receive an email within a few minutes, check your spam folder or
                contact your system administrator.
              </Typography>

              <Button
                fullWidth variant="contained"
                size="large"
                onClick={backToLogin}
                startIcon={<ArrowBackIcon />}
              >
                Back to Sign In
              </Button>
            </>
          )}

        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
